import type { GraphQLError } from 'graphql';
import type { CopyCommentsModeInput } from '@frameio/graphql-client/generated';
import { useErrorToast } from '@frameio/shared/resources/shared/hooks';
import { BULK_OPERATION_CHUNK_SIZE } from '../../../bulk-operations/constants';
import {
  useAddBulkAssetOperation,
  useHandleBulkAssetOperationFailed,
} from '../../../bulk-operations/contexts';
import { useCreateCopyAssetsOperationMutation } from '../../../bulk-operations/gql/generated/CreateCopyAssetsOperation';
import { useStartCopyAssetsOperationMutation } from '../../../bulk-operations/gql/generated/StartCopyAssetsOperation';
import type { AssetParentFragment } from '../../../shared/gql/generated/AssetParent';
import { getOperationErrorMessage } from '../../utils';
import TEXT from './generated/TEXT/useBulkCopyAssets';

TEXT.DEFINE({
  copyFailed: (assetCount: number): string =>
    `Failed to copy ${assetCount} items`,
  copyDenied: "You don't have permission to copy these items.",
});

type BulkCopyAssetsProps = {
  assetIds: string[];
  destination: AssetParentFragment;
  copyComments: CopyCommentsModeInput;
  duplicate?: boolean;
  onSuccess?: (destination: AssetParentFragment) => void;
  onError?: (error?: readonly GraphQLError[]) => void;
};

/**
 * useBulkCopyAssets is a hook that can be used to copy large batches of assets. On the BE, bulk copies
 * are processed asynchronously and in chunks, managed by an oban qeueue. The actual copying of assets
 * is not handled within the lifecycle of the request, but processed after the fact, notifying
 * the client via subscription events.
 */
export const useBulkCopyAssets = () => {
  const addErrorToast = useErrorToast();
  const addBulkAssetOperation = useAddBulkAssetOperation();
  const handlebulkAssetOperationFailed = useHandleBulkAssetOperationFailed();
  const [createCopyAssetsOperation] = useCreateCopyAssetsOperationMutation();
  const [startCopyAssetsOperation] = useStartCopyAssetsOperationMutation();

  return async ({
    assetIds,
    destination,
    copyComments,
    duplicate,
    onSuccess,
    onError,
  }: BulkCopyAssetsProps) => {
    const assetCount = assetIds.length;
    let operationId: string;

    const handleResolve = () => {
      onSuccess?.(destination);
    };

    const handleError = (error?: readonly GraphQLError[]) => {
      handlebulkAssetOperationFailed(operationId, assetCount);
      onError?.(error);
    };

    /**
     * Initiate Copy Asset Operation
     */
    await createCopyAssetsOperation({
      variables: {
        input: {
          assetIds,
          parentId: destination.id,
          copyComments,
          renameAssetCopy: duplicate,
        },
      },
    })
      .then((result) => {
        operationId = result?.data?.createCopyAssetsOperation?.assetOperation
          ?.id as string;

        if (!operationId || typeof operationId !== 'string') {
          throw new Error('Failed to create copy assets operation');
        }

        addBulkAssetOperation(operationId, {
          operationName: 'copy',
          totalAssetCount: assetCount,
          destination: duplicate
            ? undefined
            : {
                page: 'folder',
                projectId: destination.project?.id as string,
                folderAssetId: destination.id,
              },
        });

        /**
         * Execute Copy Asset Operation
         */
        startCopyAssetsOperation({
          variables: {
            input: {
              assetOperationId: operationId,
              chunkSize: BULK_OPERATION_CHUNK_SIZE,
            },
          },
        }).then(handleResolve, handleError);
      })
      .catch((error) => {
        const errorMessage = getOperationErrorMessage(error, {
          default: TEXT.copyFailed(assetCount),
          permissionDenied: TEXT.copyDenied,
        });
        addErrorToast({ title: errorMessage });
      });
  };
};
